import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService } from './auth.service';

/** setTimeout delay cap (~24.8 days) so the timer stays within the browser limit. */
const MAX_TIMEOUT_MS = 2_147_000_000;

/**
 * Leaves a protected page at the moment the access token expires.
 * Route guards block the initial navigation; this covers a session that
 * expires while Command Center (or any other shell page) is already open.
 * Those pages do not all call the API, so the 401 interceptor never runs.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private subscription: Subscription | null = null;
  private watching = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly onVisible = (): void => {
    if (document.visibilityState === 'visible') this.arm();
  };

  watchProtectedSession(): void {
    if (this.watching) return;
    this.watching = true;
    // BehaviorSubject emits during subscribe, before the assignment returns.
    this.subscription = this.auth.currentUser$.subscribe(() => this.arm());
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('visibilitychange', this.onVisible);
    }
  }

  stop(): void {
    this.watching = false;
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.clearTimer();
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.onVisible);
    }
  }

  private arm(): void {
    this.clearTimer();
    if (!this.watching) return;

    if (this.auth.hasExpiredAccessToken()) {
      this.redirectToSignIn();
      return;
    }

    const expiresAt = this.auth.getAccessTokenExpiresAt();
    if (expiresAt == null) return;

    const delay = Math.min(Math.max(0, expiresAt - Date.now()), MAX_TIMEOUT_MS);
    this.timer = setTimeout(() => {
      if (!this.watching) return;
      const due = this.auth.getAccessTokenExpiresAt();
      if (this.auth.hasExpiredAccessToken() || (due != null && due <= Date.now())) {
        this.redirectToSignIn();
        return;
      }
      this.arm();
    }, delay);
  }

  private redirectToSignIn(): void {
    const current = this.router.url;
    const returnUrl = current.startsWith('/auth') ? '/' : current;
    this.auth.expireSession();
    void this.router.navigate(['/auth/sign-in'], {
      queryParams: {
        returnUrl,
        sessionExpired: true,
      },
    });
  }

  private clearTimer(): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
